import { Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { Observable, Subject, debounceTime, distinctUntilChanged, switchMap } from 'rxjs';
import ODataFilterBuilder from 'odata-filter-builder';

import {
  IParamObj,
  IProgram,
  IProgramSearchQuery
} from '../../models';
import { ProgramService } from '../../services/program.service';

/** 
 * Odata query of Program table
 * instead of querying LookupAssociatedClassProgramChild table
 * */
@Component({
  selector: 'app-search-program-odata',
  templateUrl: './search-program-odata.component.html',
  styleUrls: ['./search-program-odata.component.scss']
})
export class SearchProgramODataComponent implements OnInit {
  @Input() currentProgram: IProgram = null;
  @Input() regionFilter: number = null;
  @Input() nonvqb5Only: boolean = false;
  @Input() searchAll: boolean = false; // If true, include ALL programs including deleted, else only isCea = true
  @Input() disabled: boolean = false;
  @Output() select$ = new EventEmitter<IProgram>();

  public isNotSearching: boolean = true;
  public searchResults$: Observable<Array<IProgram>>;
  private searchText$ = new Subject<string>();
  private oDataFilter = ODataFilterBuilder();
  public searchQuery: IProgramSearchQuery;

  constructor(private programService: ProgramService) { }

  ngOnInit() {
    this.getSearchResults();
  }

  /**
   * When change coach/region in parent component,
   * clear selected prog (switching regions so no longer valid selection)
   * and if searching, update region search filter/results
   * emit change to form
   * @param changes 
   */
  ngOnChanges(changes: SimpleChanges) {
    if (changes['regionFilter'] != null && !changes['regionFilter'].firstChange) {
      this.currentProgram = null;
      this.select$.emit(null);

      if (!this.isNotSearching) {
        this.searchQuery.region = this.regionFilter;
        this.updateSearch(null);
      }
    }
  }
  
  onSelect(selectedSearchResult: IProgram) {
    this.currentProgram = selectedSearchResult;
    this.isNotSearching = true;
    this.select$.emit(selectedSearchResult);
  }

  public getEventValue(event: Event): string {
    return (event.target as HTMLInputElement).value;
  }

  public updateSearch(searchString: string) {
    this.buildFilter();
    this.searchText$.next(searchString);
  }

  public initSearchFilters() {
    this.isNotSearching = false;
    this.searchQuery = {
      region: this.regionFilter,
      vacSiteId: '',
      name: '',
      address: '',
      created: ''
    };
  }

  private buildFilter() {
    this.oDataFilter = ODataFilterBuilder("and")
      .contains('vacSiteId', this.searchQuery.vacSiteId)
      .contains('name', this.searchQuery.name)
      .contains('address', this.searchQuery.address);
    
    if(this.searchQuery.region) {
      this.oDataFilter.eq('regionId', this.searchQuery.region);
    }

    if(this.nonvqb5Only){
      this.oDataFilter.eq('isVbq5', false);
    }

    // Default filter by Program.IsCea, Deleted unless want to search all
    if(this.searchAll === false) {
      this.oDataFilter.eq('isCea', true);
    }
  }

  /**
   * Get search results from odata Programs entity
   * Filter based on provided search parameters
   * Return top 8 results, ordered by VAC Site ID and Program name
   */
  private getSearchResults() {
    this.searchResults$ = this.searchText$.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      switchMap(() => {
        const params = { 
          $filter: this.oDataFilter.toString(),
          $orderby: 'vacSiteId asc, name asc',
          $top: 8 
        } as IParamObj;

        return this.programService.get(params)
      })
    );
  }
}
